// Validated sample questions for each agent based on role-based access
// Source: test_sample_questions.py validation results

export interface SampleQuestion {
  id: string;
  question: string;
  category: string;
  agent: 'aldo' | 'hrpolicy' | 'pwms-support' | 'product' | 'direct_embroidery';
  iconName: 'Briefcase' | 'FileText' | 'Monitor' | 'ShoppingBag' | 'ImagePlus' | 'Users' | 'Clock' | 'Shield' | 'Building' | 'HelpCircle' | 'Wrench' | 'Shirt' | 'Trousers';
}

// All validated sample questions organized by agent
export const SAMPLE_QUESTIONS: Record<string, SampleQuestion[]> = {
  // Aldo (General/Orientation) - Available to ALL users
  aldo: [
    {
      id: 'aldo-1',
      question: 'How do I set up my ADP account?',
      category: 'System Access',
      agent: 'aldo',
      iconName: 'Monitor'
    },
    {
      id: 'aldo-2',
      question: 'What benefits does UniFirst offer?',
      category: 'Benefits',
      agent: 'aldo',
      iconName: 'Shield'
    },
    {
      id: 'aldo-3',
      question: "Tell me about UniFirst's company culture",
      category: 'Company Info',
      agent: 'aldo',
      iconName: 'Building'
    },
    {
      id: 'aldo-4',
      question: 'How do I access the UniFirst mobile app?',
      category: 'System Access',
      agent: 'aldo',
      iconName: 'Monitor'
    },
    {
      id: 'aldo-5',
      question: 'What is the TLC program?',
      category: 'Programs',
      agent: 'aldo',
      iconName: 'Users'
    },
    {
      id: 'aldo-6',
      question: 'How do I contact Benefits Connect?',
      category: 'Benefits',
      agent: 'aldo',
      iconName: 'Shield'
    }
  ],

  // HR Policy Agent - Available to hr-user, hr-admin, and super-admin
  hrpolicy: [
    {
      id: 'hr-1',
      question: 'What is the attendance policy?',
      category: 'HR Policy',
      agent: 'hrpolicy',
      iconName: 'FileText'
    },
    {
      id: 'hr-3',
      question: 'Am I eligible for FMLA leave?',
      category: 'HR Policy',
      agent: 'hrpolicy',
      iconName: 'Shield'
    },
    {
      id: 'hr-4',
      question: 'What holidays does UniFirst observe?',
      category: 'HR Policy',
      agent: 'hrpolicy',
      iconName: 'Clock'
    },
    {
      id: 'hr-5',
      question: "What's the dress code policy?",
      category: 'HR Policy',
      agent: 'hrpolicy',
      iconName: 'Shirt'
    },
    {
      id: 'hr-6',
      question: 'How do I report time off?',
      category: 'HR Policy',
      agent: 'hrpolicy',
      iconName: 'FileText'
    }
  ],

  // PWMS Support Agent - Available to pwms-user, pwms-admin, and super-admin
  'pwms-support': [
    {
      id: 'pwms-1',
      question: 'How do I create a new design in PWMS?',
      category: 'PWMS',
      agent: 'pwms-support',
      iconName: 'ImagePlus'
    },
    {
      id: 'pwms-3',
      question: 'How do I track my design approval status?',
      category: 'PWMS',
      agent: 'pwms-support',
      iconName: 'FileText'
    },
    {
      id: 'pwms-5',
      question: 'How do I export my design files?',
      category: 'PWMS',
      agent: 'pwms-support',
      iconName: 'Wrench'
    },
    {
      id: 'pwms-6',
      question: 'Can Sales Reps create designs in PWMS?',
      category: 'PWMS',
      agent: 'pwms-support',
      iconName: 'Users'
    }
  ],

  // Product Agent - Available to product-user, product-admin, and super-admin
  product: [
    {
      id: 'product-1',
      question: 'Show me personalizable long sleeve shirts for childcare and shorts to go with them',
      category: 'Multi-Product',
      agent: 'product',
      iconName: 'Shirt'
    },
    {
      id: 'product-2',
      question: 'What do we have for long sleeve navy polos?',
      category: 'Product Search',
      agent: 'product',
      iconName: 'Shirt'
    },
    {
      id: 'product-4',
      question: 'What high-visibility jackets do we offer for outdoor crews?',
      category: 'Safety Gear',
      agent: 'product',
      iconName: 'Shield'
    },
    {
      id: 'product-5',
      question: 'Show me FR Jeans available for rental',
      category: 'Rental Products',
      agent: 'product',
      iconName: 'Trousers'
    },
    {
      id: 'product-6',
      question: 'Show me pants, shirts, and jackets for automotive workers',
      category: 'Industry',
      agent: 'product',
      iconName: 'Briefcase'
    },
    {
      id: 'product-7',
      question: 'Compare 123C and 11CP',
      category: 'Comparison',
      agent: 'product',
      iconName: 'FileText'
    },
    {
      id: 'product-9',
      question: 'Show me products like 04MM',
      category: 'Related Products',
      agent: 'product',
      iconName: 'ShoppingBag'
    },
    {
      id: 'product-10',
      question: 'Give me an overview of our coverall offerings',
      category: 'Overview',
      agent: 'product',
      iconName: 'FileText'
    },
    {
      id: 'product-11',
      question: 'Tell me about our various FR fabric offerings',
      category: 'Overview',
      agent: 'product',
      iconName: 'Shield'
    },
    {
      id: 'product-12',
      question: 'Show me uniforms for food service',
      category: 'Industry Top 10',
      agent: 'product',
      iconName: 'Briefcase'
    },
    {
      id: 'product-13',
      question: '(Experimental/POC) Show me catalog alternatives for "UNF-PNT Rental: Men\'s Industrial Work Pants – Navy 24 $6.85 UNF-SHT Rental: Short Sleeve Work Shirt – Gray 24 $6.10 UNF-JKT Rental: Lightweight Work Jacket – Navy 6 $9.75"',
      category: 'Invoice Analysis',
      agent: 'product',
      iconName: 'FileText'
    }
  ]
};

/**
 * Get sample questions for a user based on their accessible agents
 * @param accessibleAgentIds - Array of agent IDs the user has access to
 * @returns Array of sample questions from all accessible agents
 */
export function getSampleQuestionsForUser(accessibleAgentIds: string[]): SampleQuestion[] {
  const questions: SampleQuestion[] = [];
  
  // Always include Aldo questions (available to all users)
  questions.push(...SAMPLE_QUESTIONS.aldo);
  
  // Add questions from each accessible agent
  accessibleAgentIds.forEach(agentId => {
    const normalizedAgentId = agentId.toLowerCase();
    
    // Map agent IDs to question sets
    if (normalizedAgentId === 'hrpolicy' && SAMPLE_QUESTIONS.hrpolicy) {
      questions.push(...SAMPLE_QUESTIONS.hrpolicy);
    } else if (normalizedAgentId === 'pwms-support' && SAMPLE_QUESTIONS['pwms-support']) {
      questions.push(...SAMPLE_QUESTIONS['pwms-support']);
    } else if (normalizedAgentId === 'product' && SAMPLE_QUESTIONS.product) {
      questions.push(...SAMPLE_QUESTIONS.product);
    }
  });
  
  return questions;
}

/**
 * Shuffle array using Fisher-Yates algorithm
 * @param array - Array to shuffle
 * @returns Shuffled copy of the array
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

